/**
 * Auth middleware for oRPC
 * @package @app/api
 */

import { ORPCError, os } from '@orpc/server';
import type { Context } from './context';
import { RateLimitError } from './ratelimit';

/**
 * Base procedure with context.
 *
 * Top-level error-logging middleware: every unexpected procedure throw
 * surfaces to stderr (Vercel logs) AND the host environment's exception
 * sink (`context.captureException`, which the www handler wires to Sentry).
 *
 * Without this, oRPC's RPCHandler.handle() catches procedure errors
 * internally, encodes them as a sanitized `{code:"INTERNAL_SERVER_ERROR"}`
 * response, and the actual cause never reaches either — leaving 500s with
 * no diagnostic trail. The 2026-06-09 user.me outage went 98 hours
 * undetected for exactly this reason.
 *
 * `ORPCError` and `RateLimitError` are skipped because they are expected
 * client-error responses (NOT_FOUND, FORBIDDEN, BAD_REQUEST, rate limit)
 * thrown by routers as a control-flow mechanism, not bugs.
 *
 * captureException is provided via context (not a direct @sentry/node
 * import) so this package stays bundleable into client code — @sentry/node
 * pulls in worker_threads / path / url and crashes the Vite client build.
 */
const baseProcedure = os.$context<Context>();

export const publicProcedure = baseProcedure.use(async ({ context, next, path }) => {
  try {
    return await next();
  } catch (err) {
    const procedure = Array.isArray(path) ? path.join('.') : String(path ?? '?');
    console.error(
      `[orpc:${procedure}]`,
      err instanceof Error ? (err.stack ?? err.message) : err,
    );
    if (!(err instanceof ORPCError) && !(err instanceof RateLimitError)) {
      context.captureException(err, { tags: { orpcProcedure: procedure } });
    }
    throw err;
  }
});

/**
 * Require authentication
 */
export const protectedProcedure = publicProcedure.use(async ({ context, next }) => {
  if (!context.session) {
    // ORPCError instead of bare `new Error('Unauthorized')` so the
    // publicProcedure error filter classifies this as expected-client-error
    // (PR#154) — bare Errors land in Sentry as unexpected throws. Also
    // returns a real 401 instead of being encoded as a 500 by oRPC.
    throw new ORPCError('UNAUTHORIZED', { message: 'Unauthorized' });
  }
  return next({
    context: {
      ...context,
      session: context.session, // Now guaranteed non-null
    },
  });
});

/**
 * Protected procedure with rate limiting
 * Use this for API endpoints that should be rate limited by subscription tier
 */
export const rateLimitedProcedure = protectedProcedure.use(async ({ context, next }) => {
  const result = await context.rateLimit();

  if (!result.success) {
    throw new RateLimitError(result);
  }

  return next();
});

/**
 * Public procedure with rate limiting (for unauthenticated endpoints)
 */
export const publicRateLimitedProcedure = publicProcedure.use(async ({ context, next }) => {
  const result = await context.rateLimit();

  if (!result.success) {
    throw new RateLimitError(result);
  }

  return next();
});

/**
 * Require admin role
 */
export const adminProcedure = protectedProcedure.use(async ({ context, next }) => {
  const { session } = context;
  if (session.user.role !== 'admin' && session.user.role !== 'super-admin') {
    // See protectedProcedure above — ORPCError keeps this out of Sentry
    // (expected-client-error path) and returns a real 403.
    throw new ORPCError('FORBIDDEN', { message: 'Forbidden' });
  }
  return next();
});
